
import { create } from "zustand";

// Hash-based router for the single-route SPA.
// Routes: #/ #/login #/register #/verify #/app #/app/jobs #/app/jobs/new
// #/app/jobs/:id #/app/chat/:id #/app/trust #/app/notifications #/app/profile
// #/admin #/admin/verifications #/admin/disputes #/admin/jobs #/admin/users

export interface Route {
  path: string; // e.g. "app/jobs/abc"
  params: Record<string, string>;
}

function parseHash(): Route {
  const hash = window.location.hash.replace(/^#\/?/, "");
  return { path: hash || "", params: {} };
}

interface UiState {
  route: Route;
  initRouter: () => () => void;
  navigate: (path: string) => void;
  back: () => void;
  // ephemeral
  roleSwitcherOpen: boolean;
  setRoleSwitcherOpen: (v: boolean) => void;
}

export const useUiStore = create<UiState>((set, get) => ({
  route: { path: "", params: {} },
  roleSwitcherOpen: false,
  setRoleSwitcherOpen: (v) => set({ roleSwitcherOpen: v }),
  initRouter: () => {
    const handler = () => set({ route: parseHash() });
    handler();
    window.addEventListener("hashchange", handler);
    return () => window.removeEventListener("hashchange", handler);
  },
  navigate: (path) => {
    const clean = path.replace(/^\/+/, "");
    // Update the URL hash AND the route state synchronously. The synchronous
    // state update is critical for flows where navigate() is immediately
    // followed by another store update (e.g. setUser after OTP verify) —
    // otherwise the redirect effect would see the stale route and send the
    // user to the wrong page.
    window.location.hash = `#/${clean}`;
    set({ route: { path: clean, params: {} } });
  },
  back: () => window.history.back(),
}));

// Helper to read route segments.
export function useRouteSegments(): string[] {
  return useUiStore((s) => s.route.path.split("/").filter(Boolean));
}
